import React, { useState, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ROUTE_NAMES } from "../../routes/routeConstants";
import { API_URL } from "../../config/api";

const ResetPasswordPage: React.FC = () => {
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<"code" | "password">("code");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error" | "info">("info");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { email: stateEmail } = (location.state as { email?: string }) || {};

  const [email, setEmail] = useState(stateEmail || "");

  // Handle individual digit input
  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newCode = [...code];
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);
    const nextEmptyIndex = newCode.findIndex(c => !c);
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else {
      inputRefs.current[5]?.focus();
    }
  };

  const validatePassword = (): string | null => {
    if (newPassword.length < 8) {
      return "Password must be at least 8 characters";
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      return "Password must contain uppercase, lowercase, and a number";
    }
    if (newPassword !== confirmPassword) {
      return "Passwords do not match";
    }
    return null;
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const fullCode = code.join("");

    if (fullCode.length !== 6) {
      setMessage("Please enter all 6 digits");
      setMessageType("error");
      return;
    }

    if (!email) {
      setMessage("Please enter your email address");
      setMessageType("error");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const res = await fetch(`${API_URL}/auth/verify-reset-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.toLowerCase().trim(), code: fullCode }),
      });

      const data = await res.json();

      if (res.ok && data.verified) {
        setMessage("Code verified! Now set your new password.");
        setMessageType("success");
        setStep("password");
      } else {
        setMessage(data.message || "Invalid or expired code");
        setMessageType("error");
      }
    } catch {
      setMessage("Server error. Please try again later.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");

    const passwordError = validatePassword();
    if (passwordError) {
      setMessage(passwordError);
      setMessageType("error");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          code: code.join(""),
          newPassword,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setMessage("Password reset successful! Redirecting to login...");
        setMessageType("success");

        setTimeout(() => {
          navigate(ROUTE_NAMES.PAGES.LOGIN);
        }, 2000);
      } else {
        setMessage(data.message || "Failed to reset password");
        setMessageType("error");
      }
    } catch {
      setMessage("Server error. Please try again later.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-10">
      <div
        className="w-full max-w-md rounded-2xl border p-8"
        style={{ borderColor: "var(--twc-timberwolf)", boxShadow: "var(--shadow-soft)" }}
      >
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: "var(--twc-cambridge)", opacity: 0.2 }}
          >
            <svg
              className="w-8 h-8"
              style={{ color: "var(--twc-feldgrau)" }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
        </div>

        <h1 className="text-2xl font-bold mb-2 text-center" style={{ color: "var(--twc-ink)" }}>
          {step === "code" ? "Enter Reset Code" : "Create New Password"}
        </h1>
        <p className="text-gray-600 mb-6 text-center">
          {step === "code"
            ? "Enter the 6-digit code sent to your email"
            : "Choose a strong password for your account"}
        </p>

        {step === "code" ? (
          <form onSubmit={handleVerifyCode} className="w-full">
            {/* Email input if not provided */}
            {!stateEmail && (
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
                  style={{ borderColor: "var(--twc-timberwolf)" }}
                  disabled={loading}
                />
              </div>
            )}

            {/* 6-digit code input */}
            <div className="flex justify-center gap-2 mb-6">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { inputRefs.current[index] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className="w-12 h-14 text-center text-xl font-bold border-2 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
                  style={{
                    borderColor: digit ? "var(--twc-cambridge)" : "var(--twc-timberwolf)",
                  }}
                  disabled={loading}
                />
              ))}
            </div>

            <button
              type="submit"
              disabled={loading || code.some(d => !d)}
              className="w-full py-3 text-white font-medium rounded-lg transition-all disabled:opacity-50"
              style={{ backgroundColor: "var(--twc-cambridge)" }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Verifying...
                </span>
              ) : (
                "Verify Code"
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="w-full">
            <div className="mb-4">
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:border-blue-500 transition-colors pr-16"
                  style={{ borderColor: "var(--twc-timberwolf)" }}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-sm"
                  tabIndex={-1}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                type={showPassword ? "text" : "password"}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
                style={{ borderColor: "var(--twc-timberwolf)" }}
                disabled={loading}
              />
            </div>

            <div className="text-xs text-gray-500 mb-4">
              Password must be at least 8 characters with uppercase, lowercase, and a number.
            </div>

            <button
              type="submit"
              disabled={loading || !newPassword || !confirmPassword}
              className="w-full py-3 text-white font-medium rounded-lg transition-all disabled:opacity-50"
              style={{ backgroundColor: "var(--twc-cambridge)" }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Resetting...
                </span>
              ) : (
                "Reset Password"
              )}
            </button>
          </form>
        )}

        {message && (
          <div
            className={`mt-4 p-3 rounded-lg text-sm text-center ${
              messageType === "success"
                ? "bg-green-50 text-green-700"
                : messageType === "error"
                ? "bg-red-50 text-red-700"
                : "bg-blue-50 text-blue-700"
            }`}
          >
            {message}
          </div>
        )}

        <div className="text-center mt-6">
          <Link
            to={ROUTE_NAMES.PAGES.LOGIN}
            className="text-sm font-medium hover:underline"
            style={{ color: "var(--twc-cambridge)" }}
          >
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
